import { i18n } from '@/i18n'
import { httpClient } from '@/shared/api/httpClient'
import { assertCurrentAuthRequest } from '@/shared/api/authRequestGuard'
import type {
  ChangePasswordDto,
  ChangePayPassDto,
  NotificationSettingsDto,
  PayoutSettingsDto,
  ProfileBaseInfoResponse,
  SaveContactsDto,
  SecuritySessionResponse,
} from '@space/user-core'
import { spaceUser, spaceUserFor } from '@/modules/profile/api/spaceUser'
import { getCaptchaFieldName } from '@/shared/utils/captchaConfig'

export type {
  SaveContactsDto,
  ChangePasswordDto,
  ChangePayPassDto,
  PayoutSettingsDto,
  NotificationSettingsDto,
} from '@space/user-core'

/** Спринт 14 — канал доставки одноразового кода. */
export type OtpChoiceChannel = 'telegram' | 'email'

/**
 * Фаза выбора: бэкенд код НЕ отправил и ждёт ответа «куда прислать».
 * `channels[0]` — предвыбранный канал.
 */
export interface OtpChoice {
  channels: string[]
  /** Сессия капчи одноразовая: первую бэкенд сжёг, второй запрос несёт эту. */
  captcha_session?: string
}

/**
 * Ответ входа по коду. OTP-R6c: при включённом приложении-аутентификаторе
 * `token` — pending, полный доступ только после /profile/login-approval.
 */
export interface VerifyLoginCodeResult {
  token: string
  location: string
  requires_approval?: boolean
  approval_type?: 'otp' | 'totp'
  approval_methods?: string[]
  session_id?: number
  user_id?: number
}

/**
 * Ответ проверки кода восстановления. OTP-R6c: при включённом приложении-аутентификаторе
 * `token` — pending; recovery-pass/new примется только после /profile/login-approval.
 */
export type VerifyRecoveryCodeResult = Omit<VerifyLoginCodeResult, 'location'>

export class ProfileApi {
  // Профильные запросы собирает ядро @space/user-core, здесь — поведение Gonka.

  /** Legacy deletion: no body sends an email; the second call submits its five-character code. */
  static async requestAccountDeletionCode(): Promise<void> {
    const response = await spaceUser.requestAccountDeletionCode()
    if (response.result !== true) throw new Error(i18n.global.t('common.error'))
  }

  static async deleteAccount(code: string): Promise<void> {
    const response = await spaceUser.deleteAccount(code)
    if (response.result !== 'success') throw new Error(i18n.global.t('common.error'))
  }

  static async assignSponsor(login: string): Promise<void> {
    await httpClient.post('/profile/assign-sponsor', { login }, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
  }

  /** Get basic user info including wallet and access level */
  static getBaseInfo(isCurrent?: () => boolean): Promise<ProfileBaseInfoResponse> {
    return spaceUserFor(isCurrent).getBaseInfoResponse()
  }

  /**
   * Initialize a security action session (triggers code sending)
   * @param actionName e.g. 'change_pay_pass'
   */
  static initActionSession(actionName: string, payload: Record<string, any> = {}): Promise<SecuritySessionResponse> {
    return spaceUser.initActionSession(actionName, payload)
  }

  static updateContacts(data: SaveContactsDto): Promise<{ result: string }> {
    return spaceUser.updateContacts(data)
  }

  static changePassword(data: ChangePasswordDto): Promise<{ result: string }> {
    return spaceUser.changePassword(data)
  }

  /**
   * TASK-09 · Force-change password (for MD5 users, no old password required).
   * passwordold is omitted → backend detects requiresPasswordChange → SpaceGuard required.
   */
  static forceChangePassword(data: { passwordnew: string; passwordretry: string; confirmActionCode: string }): Promise<{ result: string }> {
    return spaceUser.forceChangePassword(data)
  }

  static changePayPassword(data: ChangePayPassDto): Promise<{ result: string }> {
    return spaceUser.changePayPassword(data)
  }

  static updatePaymentMethods(data: PayoutSettingsDto): Promise<{ result: string }> {
    return spaceUser.updatePaymentMethods(data)
  }

  static uploadAvatar(file: File): Promise<any> {
    return spaceUser.uploadAvatar(file)
  }

  static updateSettings(data: NotificationSettingsDto): Promise<{ result: string }> {
    return spaceUser.updateSettings(data)
  }

  /** Send verification code to new email (action change_email_new_email) */
  static sendEmailVerificationCode(email: string): Promise<{ result: string }> {
    return spaceUser.sendNewEmailCode(email)
  }

  static changeEmail(data: {
    email: string;
    code: string;
    confirmAction?: string;
    confirmActionCode?: string;
  }): Promise<{ result: string }> {
    return spaceUser.changeEmail(data)
  }

  /** Get mf_authorization (2FA) status */
  static getMfAuthorization(): Promise<{ enabled: boolean; totp_enabled: boolean }> {
    return spaceUser.getMfAuthorization()
  }

  /**
   * Enable/disable mf_authorization (2FA via OTP/TOTP)
   * When disabling, confirmAction and confirmActionCode are required
   */
  static setMfAuthorization(
    enabled: boolean,
    options?: { confirmAction?: string; confirmActionCode?: string }
  ): Promise<{ result: string; enabled: boolean }> {
    return spaceUser.setMfAuthorization(enabled, options)
  }

  /**
   * Check if email exists and get migration status
   * Returns a captcha session token for subsequent requests (2min TTL)
   */
  static async checkEmail(email: string, recaptchaToken?: string, isCurrent?: () => boolean): Promise<{
    exists: boolean
    isMigrated: boolean
    domain: string | null
    captchaSession: string
  }> {
    // OTP-R3: отменённая попытка не должна даже стирать чужую авторизацию.
    assertCurrentAuthRequest(isCurrent)
    // Delete auth cookie before checking email (user is not authorized if calling this endpoint)
    document.cookie = 'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

    // Clear localStorage authToken to prevent x-auth header from being sent
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
    localStorage.removeItem(storageKey)

    const payload: Record<string, string> = { email }
    if (recaptchaToken) {
      payload[getCaptchaFieldName()] = recaptchaToken
    }
    type Response = { data: { exists: boolean; isMigrated: boolean; domain: string | null; captchaSession: string } }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/check-email', isCurrent, { method: 'POST', body: JSON.stringify(payload) })
      : await httpClient.post<Response>('/profile/check-email', payload)
    return response.data
  }

  /**
   * Send login code to user's email/telegram
   * Accepts either recaptchaToken or captchaSession
   */
  static async sendLoginCode(
    email: string,
    options?: { recaptchaToken?: string; captchaSession?: string; channel?: OtpChoiceChannel; purpose?: 'verify_email' },
    isCurrent?: () => boolean
  ): Promise<{
    // Спринт 14: в фазе выбора кода нет, поэтому нет и session/reciever.
    session?: string
    reciever?: 'email' | 'telegram'
    reciever_address?: string
    choose?: OtpChoice
  }> {
    // Подтверждение текущей почты — запрос ядра: входа он не создаёт.
    if (options?.purpose === 'verify_email') return spaceUserFor(isCurrent).sendCurrentEmailCode(email)
    const payload: Record<string, string> = { email }
    if (options?.captchaSession) {
      payload['captcha-session'] = options.captchaSession
    } else if (options?.recaptchaToken) {
      payload[getCaptchaFieldName()] = options.recaptchaToken
    }
    if (options?.channel) {
      payload.channel = options.channel
    }
    type Response = { data: { session?: string; reciever?: 'email' | 'telegram'; reciever_address?: string; choose?: OtpChoice } }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/login-by-code', isCurrent, { method: 'POST', body: JSON.stringify(payload) })
      : await httpClient.post<Response>('/profile/login-by-code', payload)
    return response.data
  }

  /**
   * Спринт 14 — отправка кода подтверждения входа в выбранный канал.
   * Нужен потому, что /profile/login при двух доступных каналах код не шлёт.
   */
  static async sendLoginApprovalCode(
    channel: OtpChoiceChannel,
    isCurrent?: () => boolean,
    onPendingToken?: (token: string) => void
  ): Promise<{ reciever: 'email' | 'telegram' }> {
    type Response = { data: { reciever: 'email' | 'telegram' } }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/login-approval/send', isCurrent,
        { method: 'POST', body: JSON.stringify({ channel }) },
        ({ token }) => { if (token) onPendingToken?.(token) })
      : await httpClient.post<Response>('/profile/login-approval/send', { channel })
    return response.data
  }

  /**
   * Verify login code and authenticate user
   */
  static async verifyLoginCode(email: string, code: string, isCurrent?: () => boolean, onPendingToken?: (token: string) => void): Promise<VerifyLoginCodeResult> {
    type Response = { data: VerifyLoginCodeResult }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/login-by-code/verify', isCurrent, { method: 'POST', body: JSON.stringify({ email, code }) },
        // OTP-R6c: при включённом TOTP токен ответа — pending, его владелец — попытка входа.
        ({ token, ok, data }) => { if (ok && token && (data as Response)?.data?.requires_approval) onPendingToken?.(token) })
      : await httpClient.post<Response>('/profile/login-by-code/verify', { email, code })
    return response.data
  }

  /** Confirm the authenticated account's mailbox using the same OTP as registration, without logging in. */
  static verifyEmailCode(email: string, code: string, isCurrent: () => boolean): Promise<boolean> {
    return spaceUserFor(isCurrent).verifyCurrentEmail(email, code)
  }

  /**
   * Start password recovery process
   * Returns a captcha session token
   */
  static async startPasswordRecovery(
    email: string,
    options?: { captchaSession?: string; recaptchaToken?: string; channel?: OtpChoiceChannel },
    isCurrent?: () => boolean
  ): Promise<{
    captchaSession: string
    // Спринт 14: в фазе выбора кода нет, поэтому нет и session/reciever.
    session?: string
    reciever?: 'email' | 'telegram'
    reciever_address?: string
    choose?: OtpChoice
  }> {
    const payload: Record<string, string> = { email }
    if (options?.captchaSession) {
      payload['captcha-session'] = options.captchaSession
    } else if (options?.recaptchaToken) {
      payload[getCaptchaFieldName()] = options.recaptchaToken
    }
    if (options?.channel) {
      payload.channel = options.channel
    }
    type Response = { data: { captchaSession: string; session?: string; reciever?: 'email' | 'telegram'; reciever_address?: string; choose?: OtpChoice } }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/recovery-pass', isCurrent, { method: 'POST', body: JSON.stringify(payload) })
      : await httpClient.post<Response>('/profile/recovery-pass', payload)
    return response.data
  }

  /**
   * Verify recovery code
   */
  static async verifyRecoveryCode(email: string, code: string, isCurrent?: () => boolean, onPendingToken?: (token: string) => void): Promise<VerifyRecoveryCodeResult> {
    type Response = { data: VerifyRecoveryCodeResult }
    const response = isCurrent
      ? await httpClient.guardedRequest<Response>('/profile/recovery-pass/check', isCurrent, { method: 'POST', body: JSON.stringify({ email, code }) },
        // OTP-R6c: при включённом TOTP токен ответа — pending, его владелец — попытка восстановления.
        ({ token, ok, data }) => { if (ok && token && (data as Response)?.data?.requires_approval) onPendingToken?.(token) })
      : await httpClient.post<Response>('/profile/recovery-pass/check', { email, code })
    return response.data
  }

  /**
   * Set new password using recovery token
   */
  static async setNewPassword(data: {
    passwordnew: string
    passwordretry: string
    code: string
    token: string
    captchaSession?: string
    recaptchaToken?: string
  }, isCurrent?: () => boolean): Promise<void> {
    const payload: Record<string, string> = {
      passwordnew: data.passwordnew,
      passwordretry: data.passwordretry,
      code: data.code
    }
    if (data.captchaSession) {
      payload['captcha-session'] = data.captchaSession
    } else if (data.recaptchaToken) {
      payload[getCaptchaFieldName()] = data.recaptchaToken
    }

    const headers = { 'x-auth': data.token }
    if (isCurrent) {
      await httpClient.guardedRequest('/profile/recovery-pass/new', isCurrent, { method: 'POST', body: JSON.stringify(payload), headers })
      return
    }
    await httpClient.post('/profile/recovery-pass/new', payload, { headers })
  }
}
